import { useState } from "react";
import { toast } from "react-toastify";
import userServiceApi from "../api/userServiceApi";
import { getRequest } from "../api/http";
import { useStore } from "../store";
import { putRequest } from "../api/http";
import { setSettings, setMicroSiteActions } from "../store/actions";
import {
  invalidateMicrositeTabsCache,
  MICROSITE_PRO_SETTINGS_UPDATED_EVENT,
} from "../components/app-features/missingTabsSettingsUtils";

// Module-level cache / in-flight map so concurrent callers and remounts
// reuse the same microsite settings response without cross-site leakage.
const settingsCacheByMicrositeId = new Map();
const inFlightSettingsByMicrositeId = new Map();

/** @private test helper */
export function clearSettingsCacheForTests() {
  settingsCacheByMicrositeId.clear();
  inFlightSettingsByMicrositeId.clear();
}

const toCacheKey = (micrositeId) => String(micrositeId);

export function useSettings() {
  const { dispatch } = useStore();
  const [loading, setLoader] = useState(false);

  const getSettings = async (micrositeId, { force = false } = {}) => {
    if (!micrositeId) {
      return null;
    }

    const cacheKey = toCacheKey(micrositeId);

    if (!force && settingsCacheByMicrositeId.has(cacheKey)) {
      const cached = settingsCacheByMicrositeId.get(cacheKey);
      dispatch(setSettings(cached));
      return cached;
    }

    if (inFlightSettingsByMicrositeId.has(cacheKey)) {
      setLoader(true);
      try {
        const data = await inFlightSettingsByMicrositeId.get(cacheKey);
        if (data) {
          dispatch(setSettings(data));
        }
        return data;
      } finally {
        setLoader(false);
      }
    }

    setLoader(true);
    const partnerId = localStorage.getItem("partnerID");

    const requestPromise = getRequest(
      userServiceApi.getSettings(partnerId, micrositeId)
    )
      .then((response) => {
        const payload = response?.data;
        if (payload !== undefined && payload !== null) {
          // Preserve API payload as-is (every field / nested object).
          settingsCacheByMicrositeId.set(cacheKey, payload);
          return payload;
        }
        return null;
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to fetch site features");
        // Do not cache failures — allow retries.
        return null;
      })
      .finally(() => {
        inFlightSettingsByMicrositeId.delete(cacheKey);
      });

    inFlightSettingsByMicrositeId.set(cacheKey, requestPromise);

    try {
      const data = await requestPromise;
      if (data) {
        dispatch(setSettings(data));
      }
      return data;
    } finally {
      setLoader(false);
    }
  };

  const updateSettings = async (micrositeId, data) => {
    const partnerId = localStorage.getItem("partnerID");
    const cacheKey = toCacheKey(micrositeId);

    setLoader(true);
    try {
      const response = await putRequest(
        userServiceApi.putSettings(partnerId, micrositeId),
        data
      );
      if (response) {
        if (response.settings) {
          settingsCacheByMicrositeId.set(cacheKey, response.settings);
          dispatch(setSettings(response.settings));
        } else {
          // Response shape unknown — drop cache so next read refetches.
          settingsCacheByMicrositeId.delete(cacheKey);
        }
        dispatch(
          setMicroSiteActions({
            activeKey: micrositeId,
            menuKey: "settings",
            change: false,
          })
        );

        invalidateMicrositeTabsCache(micrositeId);
        await getSettings(micrositeId, { force: true });
        window.dispatchEvent(
          new CustomEvent(MICROSITE_PRO_SETTINGS_UPDATED_EVENT, {
            detail: { micrositeId },
          }),
        );

        toast.success("Settings updated successfully!!");
      } else {
        toast.error("Something went wrong..!");
      }
    } catch (error) {
      toast.error(error.error);
    } finally {
      setLoader(false);
    }
  };

  return {
    getSettings,
    updateSettings,
    settingsUpdateLoader: loading,
  };
}
